import type { StopHandle } from '@/util';
import type { CameraState } from '@/lib/threeScene/camera';
import { nudgeCamera } from '@/lib/threeScene/nudgeCamera';

/** Step size per arrow-key press (15°). */
const NUDGE_RADIANS = Math.PI / 12;

/**
 * Wire arrow-key handlers that orbit the given camera by 15° per
 * press. Returns `{ stop }` per the project's composable convention
 * (see docs/vue-practices.md); call `stop()` to remove the listener.
 *
 * The camera math (clamping, spherical conversion, quaternion gymnastics)
 * lives in `lib/threeScene/nudgeCamera.ts` — this composable
 * just maps key codes to nudge magnitudes.
 */
export function useCameraKeyboardShortcuts(camera: CameraState): { stop: StopHandle } {
    function onKeyDown(event: KeyboardEvent): void {
        // Skip when focus is in an editable element. Otherwise typing arrow
        // keys in a sidebar input (cup angles, stem size, etc.) would scroll
        // the camera and swallow the native cursor-movement default.
        if (isEditableTarget(event.target)) return;

        switch (event.code) {
            case 'ArrowLeft':  nudgeCamera(camera, -NUDGE_RADIANS, 0); break;
            case 'ArrowRight': nudgeCamera(camera,  NUDGE_RADIANS, 0); break;
            case 'ArrowUp':    nudgeCamera(camera, 0, -NUDGE_RADIANS); break;
            case 'ArrowDown':  nudgeCamera(camera, 0,  NUDGE_RADIANS); break;
            default: return;
        }
        event.preventDefault();
    }

    window.addEventListener('keydown', onKeyDown);
    return { stop: () => window.removeEventListener('keydown', onKeyDown) };
}

/**
 * Whether the event's focus target is an editable form element. Used to
 * suppress global keyboard shortcuts whenever the user is typing.
 */
function isEditableTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false;
    if (target.isContentEditable) return true;
    const tag = target.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}
